import { useEffect, useRef, useState } from 'react'

/// Ticks a reward-style number up smoothly between real on-chain reads, via
/// requestAnimationFrame, instead of jumping once per poll (POLL_SLOW = 30s) or per tx. Re-anchors
/// to the authoritative `value` the instant a fresh read lands - it only fills the visual gap
/// between reads, it never replaces them, so it can't drift.
///
/// `ratePerSecond` is the marginal rate at which `value` is growing RIGHT NOW - callers compute
/// this from whatever on-chain accrual formula actually governs it (e.g. HorizonFarm's
/// `userShares/totalShares * emissionsPerSecond * weightBps`, or HorizonLendingPool's
/// `userDebt/totalBorrows * borrowHorizonPerSecond` for a given reserve). Both are plain numbers
/// (already `toNumber()`'d) since this only ever feeds a display, never a transaction.
export function useLiveAccrual(value, ratePerSecond) {
  const [display, setDisplay] = useState(value)
  const anchor = useRef({ value, ts: Date.now() })

  useEffect(() => {
    anchor.current = { value, ts: Date.now() }
    setDisplay(value)
  }, [value])

  useEffect(() => {
    if (!(ratePerSecond > 0)) return
    let raf
    const tick = () => {
      const elapsedSec = (Date.now() - anchor.current.ts) / 1000
      setDisplay(anchor.current.value + ratePerSecond * elapsedSec)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [ratePerSecond])

  return display
}
